import {EddlAdobeTrackingData} from './models/eddl-adobe-tracking-data';

/**
 * Prepends a "wipe buffer" of `undefined` entries - one per product currently in `currentState` -
 * to the payload's products array, so that when the payload is pushed onto an Adobe data layer the
 * old product array elements are overwritten in the same push instead of bleeding over.
 *
 * Pure and non-mutating: returns the payload to push (a shallow copy with a new products array when
 * a buffer is needed, otherwise the original reference). `currentState` is the data layer's current
 * merged state (e.g. `adobeDataLayer.getState()`); pass `undefined`/`null` when unavailable.
 *
 * The buffer must be sized against the data layer the payload is being pushed onto. The collector
 * computes it against the iframe's own layer; the hosting page re-applies it against the surviving
 * outer layer, which has a different product count (DCP-61998).
 */
export function withWipeBuffer(
  data: EddlAdobeTrackingData,
  currentState: {ecommerce?: {products?: unknown[]}} | null | undefined
): EddlAdobeTrackingData {
  const products = data?.ecommerce?.products;
  if (products === undefined || products === null) {
    return data;
  }
  const currentLength = currentState?.ecommerce?.products?.length || 0;
  if (currentLength <= 0) {
    return data;
  }
  const wipeBuffer = new Array(currentLength).fill(undefined);
  return {
    ...data,
    ecommerce: {
      ...data.ecommerce,
      products: [...wipeBuffer, ...products],
    },
  };
}
